import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import faqApi from '../../api/faqApi';

const TrashedFAQs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [faqToActOnId, setFaqToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedFaqs = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await faqApi.getFaqs({ page, perPage, search, includeTrashed: true });
      setFaqs(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed FAQs:', err);
      setError('Failed to load trashed FAQs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setFaqs, setLastPage]);

  useEffect(() => {
    fetchTrashedFaqs(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedFaqs, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setFaqToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (faqToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await faqApi.restoreFaq(faqToActOnId);
        await fetchTrashedFaqs(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore FAQ:', err);
        setError('Failed to restore FAQ. Please try again.');
      } finally {
        setLoading(false);
        setFaqToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setFaqToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (faqToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await faqApi.permanentDeleteFaq(faqToActOnId);
        await fetchTrashedFaqs(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete FAQ:', err);
        setError('Failed to permanently delete FAQ. Please try again.');
      } finally {
        setLoading(false);
        setFaqToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setFaqToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed FAQs</h1>
        <Button
          onClick={() => navigate('/faqs')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to FAQs
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed FAQs"
          id="search-trashed-faqs"
          name="search-trashed-faqs"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by question or answer..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Question</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Active</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {faqs.length > 0 ? (
                  faqs.map((faq) => (
                    <tr key={faq.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{faq.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{faq.question}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {faq.is_active ? 'Yes' : 'No'}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(faq.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-faq-tooltip-${faq.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(faq.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-faq-tooltip-${faq.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-3 px-4 text-center text-sm text-text-gray">No trashed FAQs found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this FAQ?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this FAQ? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedFAQs;